import { Dimension, VariantItemProps } from '@design-system/variant-selector';
import uniq from 'lodash/uniq';

import { ProductPage, Variants } from '~/templates/L2ProductPage/models';
import { SalsifyProductData } from '~/types/common';
import { FIELD_NAME_VARIANT_AND_ICON } from '~/utils/constants';

import { imageMapper } from '../image/imageMapper';

const variantDimensionMapper = (
  fieldsName: string,
  productData: SalsifyProductData
): Dimension => {
  if (fieldsName !== FIELD_NAME_VARIANT_AND_ICON)
    return {
      variantName: productData[fieldsName],
    };

  const { variantName, variantIcon } = productData.Variant_And_Icon;

  return {
    variantName,
    variantIcon: variantIcon
      ? imageMapper({
          image: {
            gatsbyImageData:
              variantIcon?.localAsset?.childImageSharp?.gatsbyImageData,
            url: variantIcon.url,
          },
          alt: variantIcon.alt,
        })
      : undefined,
  } as Dimension;
};

const extractFieldOptions = (
  variants: Pick<ProductPage, 'fields'>[],
  dimensions: { dimension: string }[]
): VariantItemProps[] => {
  const validVariants = variants?.filter(
    (variant) => variant.fields.productData
  );

  const dim2Variants = dimensions[1]?.dimension
    ? uniq(
        validVariants.map((variant) =>
          dimensions[1].dimension === 'Variant_And_Icon'
            ? variant.fields.productData[dimensions[1].dimension].variantName
            : variant.fields.productData[dimensions[1].dimension]
        )
      )
    : [];

  return validVariants.map((variant) => ({
    dimension1: variantDimensionMapper(
      dimensions[0].dimension,
      variant.fields.productData
    ),
    ...(dim2Variants.length > 1 && {
      dimension2: variantDimensionMapper(
        dimensions[1].dimension,
        variant.fields.productData
      ),
    }),
    href: variant.fields.fullPath,
  }));
};

export const variantSelectorMapper = (...props) => {
  const [firstProp] = props;
  const isPDPHeader = 'variantSelector' in firstProp;

  const variantSelector: Variants = isPDPHeader
    ? firstProp.variantSelector
    : firstProp;
  const productData: SalsifyProductData = isPDPHeader
    ? firstProp.productData
    : props[2]?.salsifyExternalData;

  if (!variantSelector?.variants?.length) return null;

  const {
    labels: [{ dimensions }],
    variants,
    singleVariant,
  } = variantSelector;

  return {
    currentDimension1: productData[dimensions[0].dimension],
    currentDimension2: productData[dimensions[1]?.dimension],
    variant1Dimension: {
      label: dimensions[0].label,
      tooltip: dimensions[0].tooltip,
      fitable: dimensions[0].fitable,
    },
    variant2Dimension: {
      label: dimensions[1]?.label,
      tooltip: dimensions[1]?.tooltip,
      fitable: dimensions[1]?.fitable,
    },
    variants: extractFieldOptions(variants, dimensions),
    singleVariant,
  };
};
